import React from 'react';
import { ButtonLinkProps } from '../../../types/DesignSystem/ButtonLink';
import { ButtonVariant, IconSize } from '../../../types/DesignSystem/Button';
import { buttonVariantClass } from './Button';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';
import { BUTTON_INSET, CONTROL_GAP, CONTROL_HEIGHT } from '../controlSize';
import { useControlSize } from '../DensityProvider/DensityProvider';

/**
 * § 387 — ButtonLink: a real link in a Button's clothes. See the type file for when to reach
 * for it. The colours are `buttonVariantClass` — Button's own resolver — so the two cannot drift.
 *
 * Two things a `<button>` never needed:
 * - **`inline-flex`, not Button's `flex`.** A `<button>` shrinks to fit even when it is a block
 *   box; an `<a>` does not, and a block-level link stretches across its container.
 * - **The ink is restated under `:hover`, and the underline is removed in both states.**
 *   Bootstrap styles every anchor — `a { text-decoration: underline }` and `a:hover { color }` —
 *   and `a:hover` (one class plus an element) outranks a bare utility class, so without its own
 *   `hover:` ink a primary ButtonLink turned Bootstrap blue under the pointer.
 */

/** The hover ink per variant: each variant's REST ink, restated so `a:hover` cannot replace it. */
const HOVER_INK: Record<ButtonVariant, string> = {
  primary: 'hover:tw-text-ink-inverse',
  'danger-primary': 'hover:tw-text-ink-inverse',
  secondary: 'hover:tw-text-accent',
  tertiary: 'hover:tw-text-accent',
  subtle: 'hover:tw-text-ink',
  danger: 'hover:tw-text-tone-danger-ink',
};

/* Button's glyph boxes — the same 16 / 20 / 24px, both dialects. */
const ICON_BOX: Record<IconSize, string> = {
  small: 'tw-w-4 tw-h-4',
  medium: 'tw-w-5 tw-h-5',
  large: 'tw-w-6 tw-h-6',
  sm: 'tw-w-4 tw-h-4',
  md: 'tw-w-5 tw-h-5',
  lg: 'tw-w-6 tw-h-6',
};

const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink({
  href,
  as,
  to,
  variant = 'primary',
  size: sizeProp,
  startIcon,
  endIcon,
  iconSize = 'medium',
  external = false,
  disabled = false,
  children,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  // Plain alias, the Link spelling: the unread-prop scan reads `as` as the PROP name.
  const Tag = as ?? 'a';
  const glyph = (node: React.ReactNode) => (node ? (
    <span aria-hidden="true" className={`${ICON_BOX[iconSize]} tw-flex tw-items-center tw-justify-center`}>{node}</span>
  ) : null);

  const classes = withClassName(
    `tw-inline-flex tw-items-center tw-justify-center tw-rounded-ctl tw-text-title tw-no-underline hover:tw-no-underline ${
      CONTROL_HEIGHT[size]} ${BUTTON_INSET[size]} ${CONTROL_GAP[size]} tw-select-none tw-transition-all tw-duration-fast tw-ease-swift ${
      disabled ? '' : `motion-safe:active:tw-scale-[0.98] ${HOVER_INK[variant]}`} ${buttonVariantClass(variant, { disabled })} ${FOCUS_RING}`,
    className,
  );

  return (
    <Tag
      /* rest first — the Button.tsx rule; what the component owns comes after. */
      {...rest}
      {...(disabled
        /* A link cannot be natively disabled: no destination, still a link, and out of the tab
           order, so it is announced as an unavailable link rather than vanishing. */
        ? { role: 'link', 'aria-disabled': true, tabIndex: -1 }
        : { href, to })}
      {...(external && !disabled ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      ref={ref}
      className={classes}
      data-testid={dataTestId}
    >
      {glyph(startIcon)}
      <span className="tw-min-w-0">{children}</span>
      {glyph(endIcon)}
      {external && <span className="tw-sr-only"> (opens in new tab)</span>}
    </Tag>
  );
});

export default ButtonLink;
